import { NetworkError } from "@keycloak/keycloak-admin-client";
import { useEnvironment, type FallbackProps } from "@keycloak/keycloak-ui-shared";
import { Alert, AlertActionLink, AlertVariant, PageSection } from "@patternfly/react-core";
import { useTranslation } from "react-i18next";
import type { Environment } from "../../environment";

/**
 * Return JSX to override rendering (404/notFound cases). Otherwise return undefined
 * so the caller can fall back to the default renderer.
 */
export function customErrorRenderer({ error }: FallbackProps): JSX.Element | undefined {
  const { environment } = useEnvironment<Environment>();
  const { t } = useTranslation();

  // 1) Status-based shortcut for explicit 404s
  const statusIs404 = error instanceof NetworkError && error.response?.status === 404;

  // 2) Message-based heuristic for cases without NetworkError status
  const notFoundText = t("notFound");
  const msg = typeof error?.message === "string" ? error.message.toLowerCase() : "";
  const looksLikeNotFoundMsg =
    msg === notFoundText.toLowerCase() ||
    msg.includes("could not find the resource");

  const looksLikeNotFound = statusIs404 || looksLikeNotFoundMsg;
  if (!looksLikeNotFound) return undefined;

  // Take over: “retry” redirects to console home (breaks reload loops on blocked URLs)
  return (
    <PageSection>
      <Alert
        isInline
        variant={AlertVariant.danger}
        title={notFoundText}
        actionLinks={
          <AlertActionLink onClick={() => (location.href = environment.consoleBaseUrl)}>
            {t("retry")}
          </AlertActionLink>
        }
      />
    </PageSection>
  );
}
